import React, {useRef, useState} from 'react';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {pathToURL} from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import FormattersConstants, {FormatDataTrigger} from 'notebook/mediocre/formatted-text/FormattersConstants';
import InsertLinkModal, {Link} from 'notebook/mediocre/hyperlinks/InsertLinkModal';
import {FormatToggleButton} from 'notebook/tables/toolbar/FormatToggleButton';
import {trackSavedCmdKLinkModal} from 'notebook/util/analytics/KeyboardShortcutAnalytics';
import ModalAnchor from 'ui-library/ModalAnchor';
import {assertNever, Optional} from 'util/Types';

export interface LinkModalButtonProps {
  link: {
    text?: string;
    url?: Optional<string>;
  };
  onChange: (link: {text?: string; url?: string}) => void;
  disabled: boolean;
}

export function LinkModalButton({link, onChange, disabled}: LinkModalButtonProps): JSX.Element {
  const [showSetLinkModal, setShowSetLinkModal] = useState<{show: boolean; trigger?: FormatDataTrigger}>({
    show: false,
  });

  // Every time this component's state gets reset, generate a new eventGroupId for modal & analytics tracking.
  const eventGroupId = useRef<string>(makeAnalyticsEventId());

  const extractLinkDetails = (link: Link): {url?: string; text: string} => {
    switch (link.type) {
      case 'external':
        return {url: link.url ?? undefined, text: link.text};
      case 'internal':
        assertAndContinue(link.url != null, 'Internal link has no URL');
        return {url: link.url != null ? pathToURL(link.url) : undefined, text: link.name};
      default:
        return assertNever(link);
    }
  };

  const handleSetLink = (newLink: Link): void => {
    if (newLink == null) {
      setShowSetLinkModal({show: false});
      eventGroupId.current = makeAnalyticsEventId();
      return;
    }

    if (showSetLinkModal.trigger && showSetLinkModal.trigger === FormatDataTrigger.KEYBOARD_SHORTCUT) {
      trackSavedCmdKLinkModal({
        actionOrigin: ActionOrigin.NOTEBOOK_ANY_TABLE,
        actionMethod: ActionMethod.SAVE_MODAL,
        wasTextSelected: (link.text?.length ?? 0) !== 0,
        eventGroupId: eventGroupId.current,
      });
    }
    setShowSetLinkModal({show: false});
    onChange(extractLinkDetails(newLink));
    // Reset the ref when the modal closes.
    eventGroupId.current = makeAnalyticsEventId();
  };

  return (
    <>
      <FormatToggleButton
        active={Boolean(link.url)}
        format={FormattersConstants.LINK}
        tooltip="Hyperlink"
        icon="format-link"
        disabled={disabled}
        onClick={(_, __, trigger?: FormatDataTrigger) => {
          setShowSetLinkModal({show: true, trigger});
        }}
        metadata={{
          isHighlightedCellEmpty: (link.text?.length ?? 0) === 0,
          eventGroupId: eventGroupId.current,
        }}
      />
      {showSetLinkModal.show && (
        <ModalAnchor open={showSetLinkModal.show}>
          <InsertLinkModal
            open={showSetLinkModal.show}
            initialText={link.text}
            initialUrl={link.url}
            setLinkAndClose={handleSetLink}
          />
        </ModalAnchor>
      )}
    </>
  );
}
